import React, { useEffect, useRef } from 'react';

const BlobBg = ({ accentColor = '#c2ffe0', isDark = true, pageBg }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    resize();

    const hex2rgb = (hex) => {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [194, 255, 224];
    };
    const [ar, ag, ab] = hex2rgb(accentColor);
    const c2 = [Math.min(ar + 80, 255), ag, Math.min(ab + 60, 255)];
    const c3 = [ag, Math.min(ab + 40, 255), ar];

    // ✅ Use pageBg prop
    const bg = pageBg || (isDark ? '#080810' : '#f8f4ff');

    const blobs = [
      { px: 0.2, py: 0.3, rx: 0.28, ry: 0.22, phase: 0,   speed: 0.003,  color: [ar, ag, ab] },
      { px: 0.75, py: 0.6, rx: 0.24, ry: 0.26, phase: 2,   speed: 0.004,  color: c2 },
      { px: 0.5, py: 0.8,  rx: 0.32, ry: 0.2,  phase: 4,   speed: 0.0025, color: c3 },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      blobs.forEach((b) => {
        b.phase += b.speed;
        const cx = (b.px + Math.sin(b.phase) * 0.08) * canvas.width;
        const cy = (b.py + Math.cos(b.phase * 0.7) * 0.07) * canvas.height;
        const rx = b.rx * canvas.width * (0.9 + 0.2 * Math.sin(b.phase * 1.5));
        const ry = b.ry * canvas.height * (0.9 + 0.2 * Math.cos(b.phase * 1.3));
        const alpha = isDark ? 0.12 : 0.09;

        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
        g.addColorStop(0, `rgba(${b.color.join(',')},${alpha * 2})`);
        g.addColorStop(0.5, `rgba(${b.color.join(',')},${alpha})`);
        g.addColorStop(1, `rgba(${b.color.join(',')},0)`);

        ctx.save();
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, b.phase * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.restore();
      });

      ctx.fillStyle = isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = `rgba(${ar},${ag},${ab},${isDark ? 0.025 : 0.05})`;
      ctx.lineWidth = 1;
      const step = 80;
      for (let x = 0; x < canvas.width; x += step) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += step) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, [accentColor, isDark, pageBg]);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />;
};

export default BlobBg;